import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useCreatePatient } from '@/hooks/usePatients';
import { patientMutationErrorText } from '@/lib/patientMutationError';
import { canEnterPatientCpr, isValidCprEntry } from '@/lib/cprEntry';
import { contactFieldErrorTexts } from '@/lib/contactFields';
import {
  applyDefaultTelephoneTypes,
  emptyTelephoneDraft,
  firstStamkortFocusId,
  focusPatientField,
  stamkortFieldErrorTexts,
  stamkortPayload,
  type TelephoneSubscriberDraft,
} from '@/lib/patientStamkortFields';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  PatientStamkortFields,
  stamkortMessagesFromT,
} from '@/components/patient/PatientStamkortFields';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { FormPage, FormSection } from '@/components/ui/FormPage';
import { PageLayout } from '@/components/ui/PageLayout';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';

/**
 * New patient form. Mirrors v1's `Clinio/src/pages/clinic/NewPatient.tsx`
 * field set: name, email, Telefonnumre, CPR, address. CPR is optional and goes
 * straight to the create-patient Edge Function which encrypts it before
 * it ever lands on disk.
 */
export function NewPatient() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const create = useCreatePatient();
  const canSetCpr = Boolean(
    activeClinicId &&
      canEnterPatientCpr((resource, action) => hasPermission(activeClinicId, resource, action), 'create'),
  );

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [telephoneSubscribers, setTelephoneSubscribers] = useState<TelephoneSubscriberDraft[]>([
    emptyTelephoneDraft(),
  ]);
  const [cpr, setCpr] = useState('');
  const [street, setStreet] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Danmark');
  const [notes, setNotes] = useState('');
  const [marketingEmail, setMarketingEmail] = useState(false);
  const [marketingSms, setMarketingSms] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldsSubmitted, setFieldsSubmitted] = useState(false);
  const fieldMessages = {
    invalidEmail: t('patients.errors.invalid_email'),
    invalidPhone: t('patients.errors.invalid_phone'),
  };
  const fieldErrors = contactFieldErrorTexts(fieldsSubmitted, email, '', fieldMessages);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const subscribers = applyDefaultTelephoneTypes(telephoneSubscribers);
    if (subscribers.some((row, index) => row.code !== telephoneSubscribers[index]?.code)) {
      setTelephoneSubscribers(subscribers);
    }
    const fields = contactFieldErrorTexts(true, email, '', fieldMessages);
    const stamkort = stamkortFieldErrorTexts(
      true,
      jobTitle,
      subscribers,
      stamkortMessagesFromT(t),
    );

    if (!activeClinicId) {
      setSubmitError(t('patients.errors.no_clinic'));
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError(t('patients.errors.name_required'));
      focusPatientField('first_name');
      return;
    }

    if (canSetCpr && cpr.trim().length > 0 && !isValidCprEntry(cpr)) {
      setSubmitError(t('patients.errors.cpr_format'));
      focusPatientField('cpr');
      return;
    }

    if (!fields.canSave || !stamkort.canSave) {
      setFieldsSubmitted(true);
      setSubmitError(t('patients.errors.invalid_input'));
      focusPatientField(fields.email ? firstStamkortFocusId(stamkort) : 'email');
      return;
    }

    const medcom = stamkortPayload(jobTitle, subscribers);

    setSubmitting(true);
    try {
      const created = await create.mutateAsync({
        clinic_id: activeClinicId,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim() || undefined,
        cpr: canSetCpr ? cpr.trim() || undefined : undefined,
        address: {
          street: street.trim() || undefined,
          city: city.trim() || undefined,
          zip_code: zipCode.trim() || undefined,
          country: country.trim() || undefined,
        },
        notes: notes.trim() || undefined,
        marketing_consent_email: marketingEmail,
        marketing_consent_sms: marketingSms,
        job_title: medcom.job_title,
        telephone_subscribers: medcom.telephone_subscribers,
      });
      toast.success(
        t('patients.toast_created', { name: `${created.first_name} ${created.last_name}` }),
      );
      navigate(`/patients/${created.id}`);
    } catch (err) {
      setSubmitError(patientMutationErrorText(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <form onSubmit={onSubmit}>
        <FormPage
          breadcrumb={[
            { label: t('patients.title'), to: '/patients' },
            { label: t('patients.new_title') },
          ]}
          title={t('patients.new_title')}
          onCancel={() => navigate('/patients')}
          primary={
            <Button
              type="submit"
              loading={submitting}
              loadingText={t('common.saving')}
              disabled={!activeClinicId}
            >
              {t('patients.save_new')}
            </Button>
          }
        >
          {submitError ? <ErrorAlert message={submitError} /> : null}

          <FormSection title={t('patients.section_basic')}>
            <div className="grid gap-s4 nav:grid-cols-2">
              <Input
                label={t('patients.field_first_name')}
                name="first_name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <Input
                label={t('patients.field_last_name')}
                name="last_name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
              <Input
                label={t('patients.field_email')}
                name="email"
                type="text"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={fieldErrors.email}
              />
            </div>
          </FormSection>

          <PatientStamkortFields
            presentation="cards"
            jobTitle={jobTitle}
            onJobTitleChange={setJobTitle}
            subscribers={telephoneSubscribers}
            onSubscribersChange={setTelephoneSubscribers}
            submitted={fieldsSubmitted}
          />

          {canSetCpr ? (
            <FormSection title={t('patients.section_cpr')}>
              <Input
                label={t('patients.field_cpr')}
                name="cpr"
                value={cpr}
                onChange={(e) => setCpr(e.target.value)}
                hint={t('patients.cpr_encryption_note')}
              />
            </FormSection>
          ) : null}

          <FormSection title={t('patients.section_address')}>
            <div className="grid gap-s4 nav:grid-cols-2">
              <div className="nav:col-span-2">
                <Input
                  label={t('patients.field_street')}
                  name="street"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                />
              </div>
              <Input
                label={t('patients.field_zip')}
                name="zip_code"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
              />
              <Input
                label={t('patients.field_city')}
                name="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
              <div className="nav:col-span-2">
                <Input
                  label={t('patients.field_country')}
                  name="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>
            </div>
          </FormSection>

          <FormSection title={t('patients.section_notes')}>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('patients.field_notes_placeholder')}
            />
          </FormSection>

          <FormSection title={t('patients.section_consent')}>
            <div className="flex flex-col gap-s3">
              <Checkbox
                checked={marketingEmail}
                onChange={(e) => setMarketingEmail(e.target.checked)}
                label={t('patients.consent_marketing_email')}
                hint={t('patients.consent_marketing_email_desc')}
              />
              <Checkbox
                checked={marketingSms}
                onChange={(e) => setMarketingSms(e.target.checked)}
                label={t('patients.consent_marketing_sms')}
                hint={t('patients.consent_marketing_sms_desc')}
              />
            </div>
          </FormSection>
        </FormPage>
      </form>
    </PageLayout>
  );
}
